import './set-public-path.js';
import React from 'react';
import ReactDOM from 'react-dom';
import { property } from 'lodash';
import singleSpaReact from 'single-spa-react';
import singleSpaCanopy from 'single-spa-canopy';
import StatusInputComponent from './common/status-input.component.js';
import StatusCell from './dashboard/table/status-cell.component.js';
import { reminderIntervalsMap } from './common/reminder-picker/reminder.constants.js';
import { useDraggableList as useDraggableListExport } from './common/cp-draggable';

// Single spa app functions
const reactLifecycles = singleSpaReact({
  React,
  ReactDOM,
  loadRootComponent: () => import(/*webpackChunkName: "tasks-app" */ './root.component.js').then(property('default')),
  domElementGetter,
});

const togglesToFetch = [
  'task_template_description',
  'new_contact_api',
  'date_columns',
  'customize_task_list',
  'toggle_transcripts_module',
  'task_dash_filter_tabs',
  'task_workspace_automation',
  'ft_create_edit_modal',
  'task_template_redesign',
];

const sscConfig = {
  domElementGetter,
  childAppName: 'tasks-ui',
  mainContentTransition: false,
  featureToggles: togglesToFetch,
  setPublicPath(publicPath) {
    __webpack_public_path__ = publicPath;
  },
};

const canopyLifecycles = singleSpaCanopy(sscConfig);

export let RichTextEditor = () => null,
  getDefaultRichText = () => {},
  serializeRichText = () => {},
  deserializeRichText = () => {},
  sanitizeHTML = () => {};

export const bootstrap = [
  canopyLifecycles.bootstrap,
  reactLifecycles.bootstrap,
  () => {
    return SystemJS.import('rich-text-ui!sofe').then((RichUI) => {
      RichTextEditor = RichUI.default;
      getDefaultRichText = RichUI.getDefaultRichText;
      serializeRichText = RichUI.serializeRichText;
      deserializeRichText = RichUI.deserializeRichText;
      sanitizeHTML = RichUI.sanitizeHTML;
      return Promise.resolve();
    });
  },
];

export const mount = [reactLifecycles.mount, canopyLifecycles.mount];

export const unmount = [reactLifecycles.unmount, canopyLifecycles.unmount];

export const unload = [canopyLifecycles.unload];

function domElementGetter() {
  let el = document.getElementById('tasks-ui');
  if (!el) {
    el = document.createElement('div');
    el.setAttribute('id', 'tasks-ui');
    document.body.insertBefore(el, document.body.childNodes[0]);
  }
  return el;
}

// We have some things that need to fetch more than one toggle
function getFeatureToggle(toggle) {
  return SystemJS.import('feature-toggles!sofe').then((mod) => mod.fetchFeatureToggles(...toggle));
}

export function callServiceModalObservable() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "callServiceModalObservable" */ './create-edit-task/create-edit-task.wrapper.js').then(
      property('callServiceModal')
    )
  );
}

// Code split version of createEditTaskModal
export function createEditTaskObservable() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "createEditTaskObservable" */ './create-edit-task/create-edit-task.wrapper.js').then(
      property('createEditTaskModal')
    )
  );
}

//Temporarily exporting some components here.
//I'd like these to migrate to a single-spa parcels ASAP
//As we're dealing directly with tasks in notices and soon more places
//We're going to need a clean way to share these. Parcels is the long
//term solution, but in the short term I believe we need to do this
export const ReactStatusInput = StatusInputComponent;
export const ReactStatusInputCell = StatusCell;

export function getStatusInputParcel() {
  return import(/* webpackChunkName: "statusInputParcel" */ './parcels/status-input.parcel.js').then(
    property('statusInputParcel')
  );
}

export function getStatusCellParcel() {
  return import(/* webpackChunkName: "statusCellParcel" */ './parcels/status-cell.parcel.js').then(
    property('statusCellParcel')
  );
}

export function getTimeLineParcel() {
  return import(/* webpackChunkName: "timelineParcel" */ './parcels/timeline.parcel.js').then(
    property('timelineParcel')
  );
}

export function getServiceModalParcel() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "ServiceModalParcel" */ './parcels/service-modal.parcel.js').then(
      property('createEditTask')
    )
  );
}

export function getCreateEditTaskParcel() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "CreateEditTaskParcel" */ './parcels/create-edit-task.parcel.js').then(
      property('createEditTask')
    )
  );
}

export const defaultIntervalMap = reminderIntervalsMap;

export function getReminderPickerParcel() {
  return import(/* webpackChunkName: "CreateEditTaskParcel" */ './parcels/reminder-picker.js').then(
    property('reminderPickerParcel')
  );
}

export function getTaskLink() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "taskLink" */ './parcels/task-link-parcel.js').then(property('taskLinkParcel'))
  );
}

export function getClientRequestModal() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(
      /* webpackChunkName: 'ClientRequestModal' */ 'src/service-workspace/subtasks-section/client-request-modal/client-request-modal.component.js'
    ).then((mod) => mod.default)
  );
}

export function getClientRequestModalObservable() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "getClientRequestModal" */ './parcels/client-request-modal-obs').then(
      property('showClientRequestModal')
    )
  );
}

export function getTasksList() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "tasks-list-component" */ './exports/tasks-list-component').then((mod) => mod)
  );
}

export function getInvalidAssigneesBanner() {
  return getFeatureToggle(togglesToFetch).then(() =>
    import(/* webpackChunkName: "invalid-assignees-banner-component" */ './common/invalid-asignees-resolve/invalid-assignees-banner.component').then(mod => mod)
  )
}

export const useDraggableList = useDraggableListExport;
